import { useCallback, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { beginLogin } from '../auth';
import { useCapabilities } from '../capabilities';
import { withSupport } from '../utils/support';
import { deleteReport, NotFoundError, UnauthorizedError } from '../api';

export interface DeleteReportButtonProps {
  reportId: string;
  onDeleted: () => void;
}

export function DeleteReportButton({ reportId, onDeleted }: DeleteReportButtonProps) {
  const { canManageScopes } = useCapabilities();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const doDelete = useCallback(async () => {
    setDeleting(true);
    setError(undefined);
    try {
      await deleteReport(reportId);
      setConfirming(false);
      onDeleted();
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await beginLogin();
        return;
      }
      setError(
        withSupport(
          err instanceof NotFoundError
            ? 'Report not found, or outside your access scopes.'
            : err instanceof Error
              ? err.message
              : 'Could not delete report.',
        ),
      );
    } finally {
      setDeleting(false);
    }
  }, [reportId, onDeleted]);

  if (!canManageScopes) return null;

  return (
    <>
      <Button iconName="remove" onClick={() => setConfirming(true)}>
        Delete report
      </Button>

      {confirming && (
        <Modal
          visible
          onDismiss={() => {
            if (!deleting) setConfirming(false);
          }}
          header="Delete report?"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setConfirming(false)} disabled={deleting}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={() => void doDelete()} loading={deleting}>
                  Delete report
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="s">
            <span>This permanently deletes the report and its attachments. This can&apos;t be undone.</span>
            {error && <StatusIndicator type="error">{error}</StatusIndicator>}
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
}
